import React, { useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge } from 'react-bootstrap';
import {
  FaFileUpload, FaFilePdf, FaTrash, FaCheckCircle,
  FaTv, FaPlayCircle, FaClock, FaUserTie
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../services/api';

import { PageHeader }      from '../../components/shared/PageHeader';
import { KpiCards }        from '../../components/shared/KpiCard';
import { SearchFilterBar } from '../../components/shared/SearchFilterBar';
import { DataTable }       from '../../components/shared/DataTable';

const FILTERS = [
  { value: 'ALL',      label: 'Todas',         variant: 'dark'    },
  { value: 'SELECTED', label: 'Selecionadas',  variant: 'success' },
  { value: 'WIDGETS',  label: 'Widgets',       variant: 'warning' },
  { value: 'EMPTY',    label: 'Espaço vago',   variant: 'danger'  },
];

const COLUMNS = ['', 'Mídia', 'Tela', 'Início', 'Fim', 'Exibições', 'Duração', 'Dias'];

const WIDGET_NAMES = ['digital clock', 'advanced weather', 'analog clock'];
const EMPTY_NAMES  = ['anuncie aqui', 'espaco vago', 'espaço vago'];

// 🎨 Paleta padrão (Maxtra) — sobrescrita por empresa.primaryColor se existir
const DEFAULT_PRIMARY = [140, 0, 0];      // #8c0000 — vermelho Maxtra
const DEFAULT_DARK    = [33, 37, 41];     // preto suave
const DEFAULT_ACCENT  = [218, 165, 32];   // dourado (autoridade)

export default function ComprovanteVeiculacao() {
  const [medias,        setMedias]        = useState([]);
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [searchTerm,    setSearchTerm]    = useState('');
  const [filter,        setFilter]        = useState('ALL');
  const [clientes,      setClientes]      = useState([]);
  const [empresa,       setEmpresa]       = useState(null);
  const [clienteId,     setClienteId]     = useState('');
  const [importing,     setImporting]     = useState(false);
  const [generating,    setGenerating]    = useState(false);
  const [fileName,      setFileName]      = useState('');

  useEffect(() => {
    api.get('/customers')
      .then(res => setClientes(res.data.filter(c => c.isActive)))
      .catch(() => toast.error('Erro ao carregar clientes.'));

    api.get('/company/settings')
      .then(res => setEmpresa(res.data))
      .catch(() => console.warn('Não foi possível carregar dados da empresa.'));
  }, []);

  // ──────────────────────────────────────────
  // 🧹 PARSER DO CSV DA VNNOX
  // ──────────────────────────────────────────
  const cleanVnnoxValue = (val) => {
    if (val === null || val === undefined) return '';
    let s = String(val).trim();
    if (s.startsWith('"') && s.endsWith('"')) s = s.slice(1, -1);
    s = s.replace(/""/g, '"').replace(/^=\(/, '').replace(/\)$/, '').replace(/^"+|"+$/g, '');
    return s.trim();
  };

  const splitCsvLine = (line) => {
    const result = []; let current = ''; let inQuotes = false; let i = 0;
    while (i < line.length) {
      const ch = line[i]; const next = line[i + 1];
      if (ch === '"' && inQuotes && next === '"') { current += '""'; i += 2; continue; }
      if (ch === '"') { inQuotes = !inQuotes; current += ch; i++; continue; }
      if ((ch === ',' || ch === ';' || ch === '\t') && !inQuotes) { result.push(current); current = ''; i++; continue; }
      current += ch; i++;
    }
    result.push(current);
    return result;
  };

  const parseFile = async (file) => {
    setImporting(true);
    try {
      let text = await file.text();
      text = text.replace(/^\uFEFF/, '');
      const lines = text.split(/\r\n|\n|\r/).map(l => l.trim()).filter(l => l.length > 0);
      if (lines.length < 2) { toast.error('Arquivo vazio ou inválido.'); return; }

      const parsed = lines.slice(1).map((line, idx) => {
        const cols = splitCsvLine(line).map(cleanVnnoxValue);
        const mediaName  = cols[0] || '';
        const screenName = cols[1] || '';
        const startDate  = cols[2] || '';
        const endDate    = cols[3] || '';
        const duration   = parseInt(cols[4] || '0', 10) || 0;
        const times      = parseInt(cols[5] || '0', 10) || 0;
        const days       = parseInt(cols[6] || '0', 10) || 0;
        const lower = mediaName.toLowerCase();
        const isWidget = WIDGET_NAMES.some(w => lower.includes(w));
        const isEmpty  = EMPTY_NAMES.some(e => lower.includes(e));
        return { id: `${idx}-${mediaName}`, mediaName, screenName, startDate, endDate, duration, times, days, isWidget, isEmpty };
      }).filter(m => m.mediaName);

      if (!parsed.length) { toast.error('Nenhuma mídia válida encontrada.'); return; }

      setMedias(parsed); setSelectedIds(new Set()); setFileName(file.name);
      toast.success(`✅ ${parsed.length} mídias importadas com sucesso!`);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao processar arquivo.');
    } finally {
      setImporting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    parseFile(file);
    e.target.value = '';
  };

  const handleClearAll = () => {
    if (!medias.length) return;
    if (!window.confirm('Limpar todas as mídias importadas?')) return;
    setMedias([]); setSelectedIds(new Set()); setFileName('');
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredMedias = useMemo(() => {
    const s = searchTerm.toLowerCase();
    return medias.filter(m => {
      const matchText = m.mediaName.toLowerCase().includes(s) || m.screenName.toLowerCase().includes(s);
      let matchFilter = true;
      if (filter === 'SELECTED') matchFilter = selectedIds.has(m.id);
      if (filter === 'WIDGETS')  matchFilter = m.isWidget;
      if (filter === 'EMPTY')    matchFilter = m.isEmpty;
      return matchText && matchFilter;
    });
  }, [medias, searchTerm, filter, selectedIds]);

  const toggleSelectAll = () => {
    const visibleIds = filteredMedias.map(m => m.id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const selectedMedias = medias.filter(m => selectedIds.has(m.id));
  const totalExibicoes = selectedMedias.reduce((acc, m) => acc + m.times, 0);
  const totalDuracao   = selectedMedias.reduce((acc, m) => acc + m.duration, 0);

  const formatDuration = (sec) => {
    if (!sec) return '0s';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}h ${m}min`;
    if (m > 0) return `${m}min ${s}s`;
    return `${s}s`;
  };

  const formatDate = (str) => {
    if (!str) return '-';
    const d = new Date(str.replace(' ', 'T'));
    if (isNaN(d)) return str;
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const kpiItems = [
    { label: 'Mídias importadas',  value: medias.length,                          color: 'primary' },
    { label: 'Selecionadas',       value: selectedMedias.length,                  color: 'success' },
    { label: 'Total de exibições', value: totalExibicoes.toLocaleString('pt-BR'), color: 'info'    },
    { label: 'Tempo total',        value: formatDuration(totalDuracao),           color: 'warning' },
  ];

  // ──────────────────────────────────────────
  // 🎨 Helper: converte hex em [r,g,b]
  // ──────────────────────────────────────────
  const hexToRgb = (hex) => {
    if (!hex) return null;
    const h = hex.replace('#', '');
    if (h.length !== 6) return null;
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ];
  };

  // ──────────────────────────────────────────
  // 📄 GERAR PDF — COM GATILHOS MENTAIS 🧠
  // ──────────────────────────────────────────
  const handleGenerate = () => {
    if (!selectedMedias.length) { toast.warn('Selecione pelo menos uma mídia.'); return; }
    if (!clienteId)             { toast.warn('Selecione um cliente.'); return; }

    setGenerating(true);
    try {
      const cliente    = clientes.find(c => String(c.id) === String(clienteId));
      const screenName = selectedMedias[0]?.screenName || '-';

      const datas = selectedMedias.flatMap(m => [m.startDate, m.endDate]).filter(Boolean);
      const sorted = [...datas].sort();
      const periodoIni = sorted[0]?.split(' ')[0] || '';
      const periodoFim = sorted[sorted.length - 1]?.split(' ')[0] || '';

      const fmt = (s) => {
        if (!s) return '-';
        const parts = s.split('-');
        if (parts.length !== 3) return s;
        const [y, m, d] = parts;
        return `${d}/${m}/${y}`;
      };

      // 🎨 Paleta dinâmica (usa cor da empresa se houver, senão Maxtra)
      const PRIMARY = hexToRgb(empresa?.primaryColor) || DEFAULT_PRIMARY;
      const DARK    = DEFAULT_DARK;
      const ACCENT  = DEFAULT_ACCENT;

      // Endereço da empresa
      const enderecoEmpresa = [empresa?.street, empresa?.number && `nº ${empresa.number}`, empresa?.neighborhood].filter(Boolean).join(', ');
      const cidadeEmpresa   = [empresa?.city, empresa?.state].filter(Boolean).join(' - ');

      const doc = new jsPDF();

      // ═════════════════════════════════════════════════════
      // 🎨 CABEÇALHO PROFISSIONAL
      // ═════════════════════════════════════════════════════
      doc.setFillColor(...PRIMARY);
      doc.rect(0, 0, 210, 38, 'F');

      // Faixa decorativa inferior (dourada — autoridade)
      doc.setFillColor(...ACCENT);
      doc.rect(0, 38, 210, 1.5, 'F');

      // Nome da empresa
      doc.setTextColor(255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      const nomeEmpresa = (empresa?.name || 'EMPRESA').toUpperCase();
      doc.text(nomeEmpresa, 14, 14);

      // Razão social
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      if (empresa?.corporateName && empresa.corporateName !== empresa.name) {
        doc.text(empresa.corporateName, 14, 19);
      }

      // Subtítulo
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('COMPROVANTE OFICIAL DE VEICULAÇÃO', 14, 30);

      // Info da empresa (direita)
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      let yRight = 10;
      if (empresa?.cnpj)     { doc.text(`CNPJ: ${empresa.cnpj}`, 196, yRight, { align: 'right' }); yRight += 4; }
      if (enderecoEmpresa)   { doc.text(enderecoEmpresa, 196, yRight, { align: 'right' }); yRight += 4; }
      if (cidadeEmpresa)     { doc.text(cidadeEmpresa, 196, yRight, { align: 'right' }); yRight += 4; }
      if (empresa?.phone)    { doc.text(`Tel: ${empresa.phone}`, 196, yRight, { align: 'right' }); yRight += 4; }
      if (empresa?.email)    { doc.text(empresa.email, 196, yRight, { align: 'right' }); }

      // ═════════════════════════════════════════════════════
      // 📋 DADOS DO CLIENTE
      // ═════════════════════════════════════════════════════
      doc.setTextColor(0);
      doc.setFontSize(10);
      let y = 50;

      const drawField = (label, value) => {
        doc.setFont('helvetica', 'bold'); doc.text(label, 14, y);
        doc.setFont('helvetica', 'normal'); doc.text(value || '-', 38, y);
        y += 6;
      };

      drawField('Cliente:',  cliente?.name);
      drawField('CNPJ/CPF:', cliente?.cnpj);
      drawField('Período:',  `${fmt(periodoIni)} a ${fmt(periodoFim)}`);
      drawField('Tela:',     screenName);

      // ═════════════════════════════════════════════════════
      // 🧠 PROVA SOCIAL: EXIBIÇÕES + TEMPO DE EXIBIÇÃO
      // ═════════════════════════════════════════════════════
      y += 4;
      doc.setFillColor(...PRIMARY);
      doc.roundedRect(14, y, 182, 32, 3, 3, 'F');

      // ── Lado esquerdo: EXIBIÇÕES ─────────────────────
      doc.setTextColor(255);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text(totalExibicoes.toLocaleString('pt-BR'), 60, y + 16, { align: 'center' });

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('EXIBIÇÕES NO PERÍODO', 60, y + 22, { align: 'center' });

      // ── Linha separadora ─────────────────────────────
      doc.setDrawColor(255);
      doc.setLineWidth(0.3);
      doc.line(115, y + 5, 115, y + 25);

      // ── Lado direito: TEMPO DE EXIBIÇÃO ──────────────
      doc.setFontSize(26);
      doc.setFont('helvetica', 'bold');
      doc.text(formatDuration(totalDuracao), 165, y + 16, { align: 'center' });

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('TEMPO TOTAL DE EXIBIÇÃO', 165, y + 22, { align: 'center' });

      // ── Subtítulo do bloco ───────────────────────────
      doc.setFontSize(7);
      doc.text(
        'Sua marca em destaque, com presença real e mensurável no ponto de exibição',
        105, y + 28, { align: 'center' }
      );

      y += 40;

      // ═════════════════════════════════════════════════════
      // 📊 TABELA DE MÍDIAS
      // ═════════════════════════════════════════════════════
      autoTable(doc, {
        startY: y,
        head: [['Mídia', 'Exibições', 'Duração', 'Dias']],
        body: selectedMedias.map(m => [
          m.mediaName,
          m.times.toLocaleString('pt-BR'),
          formatDuration(m.duration),
          m.days,
        ]),
        foot: [[
          'TOTAL',
          totalExibicoes.toLocaleString('pt-BR'),
          formatDuration(totalDuracao),
          '',
        ]],
        theme: 'striped',
        headStyles: { fillColor: DARK, textColor: 255 },
        footStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
      });

      const finalY = doc.lastAutoTable.finalY || 100;

      // ═════════════════════════════════════════════════════
      // 🚀 CTA PODEROSO
      // ═════════════════════════════════════════════════════
      const ctaY = finalY + 8;
      doc.setFillColor(...DARK);
      doc.roundedRect(14, ctaY, 182, 24, 3, 3, 'F');

      // Faixa lateral colorida
      doc.setFillColor(...PRIMARY);
      doc.roundedRect(14, ctaY, 4, 24, 3, 3, 'F');

      doc.setTextColor(255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Quer DOBRAR o alcance da sua marca no próximo mês?', 22, ctaY + 9);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(220);
      const contatoLinha = [empresa?.phone, empresa?.email].filter(Boolean).join('  •  ');
      doc.text(`Fale com a gente: ${contatoLinha || 'entre em contato'}`, 22, ctaY + 16);

      doc.setFontSize(7);
      doc.setTextColor(...ACCENT);
      doc.setFont('helvetica', 'bold');
      doc.text('AMPLIE SUA PRESENÇA  •  AUMENTE SUAS VENDAS  •  CRESÇA COM A GENTE', 22, ctaY + 21);

      // ═════════════════════════════════════════════════════
      // 🦶 RODAPÉ
      // ═════════════════════════════════════════════════════
      const rodapeY = ctaY + 32;
      doc.setDrawColor(200);
      doc.setLineWidth(0.2);
      doc.line(14, rodapeY, 196, rodapeY);

      doc.setFontSize(7);
      doc.setTextColor(120);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Documento gerado em ${new Date().toLocaleString('pt-BR')}`,
        14, rodapeY + 5
      );
      doc.text(
        `${empresa?.name || 'Sistema'} • Comprovante oficial de veiculação`,
        196, rodapeY + 5, { align: 'right' }
      );

      const filename = `comprovante_${cliente?.name?.replace(/\s+/g, '_') || 'cliente'}_${periodoIni}.pdf`;
      doc.save(filename);
      toast.success('📄 PDF gerado com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao gerar PDF.');
    } finally {
      setGenerating(false);
    }
  };

  const allVisibleSelected = filteredMedias.length > 0 && filteredMedias.every(m => selectedIds.has(m.id));

  return (
    <Container fluid className="p-4">
      <PageHeader title="Comprovante de Veiculação" />

      <Card className="mb-4 shadow-sm border-0">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <h6 className="mb-2"><FaFileUpload className="me-2 text-primary" /> Importar planilha do VNNOX</h6>
              <Form.Control type="file" accept=".csv,.txt" onChange={handleFileChange} disabled={importing} />
              {fileName && (
                <small className="text-muted d-block mt-2">
                  <FaCheckCircle className="text-success me-1" /> {fileName}
                </small>
              )}
            </Col>
            <Col md={6} className="text-md-end mt-3 mt-md-0">
              {importing && <Spinner animation="border" size="sm" className="me-2" />}
              {medias.length > 0 && (
                <Button variant="outline-danger" size="sm" onClick={handleClearAll}>
                  <FaTrash className="me-1" /> Limpar tudo
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {medias.length > 0 && <Row className="mb-4"><KpiCards items={kpiItems} colSize={3} /></Row>}

      {medias.length > 0 && (
        <SearchFilterBar
          searchTerm={searchTerm} onSearchChange={setSearchTerm}
          searchPlaceholder="Buscar por mídia ou tela..."
          filters={FILTERS} activeFilter={filter} onFilterChange={setFilter}
        />
      )}

      {medias.length > 0 && (
        <DataTable
          columns={[
            <Form.Check key="cb" type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />,
            ...COLUMNS.slice(1),
          ]}
          loading={false}
          emptyMessage="Nenhuma mídia encontrada."
        >
          {filteredMedias.map(m => (
            <tr key={m.id} className={selectedIds.has(m.id) ? 'table-success' : ''}
                style={{ cursor: 'pointer' }} onClick={() => toggleSelect(m.id)}>
              <td onClick={(e) => e.stopPropagation()}>
                <Form.Check type="checkbox" checked={selectedIds.has(m.id)} onChange={() => toggleSelect(m.id)} />
              </td>
              <td>
                <strong>{m.mediaName}</strong>
                {m.isWidget && <Badge bg="warning" className="ms-2">Widget</Badge>}
                {m.isEmpty  && <Badge bg="danger"  className="ms-2">Espaço vago</Badge>}
              </td>
              <td className="text-muted small"><FaTv className="me-1" /> {m.screenName}</td>
              <td className="small">{formatDate(m.startDate)}</td>
              <td className="small">{formatDate(m.endDate)}</td>
              <td><FaPlayCircle className="text-info me-1" /><strong>{m.times.toLocaleString('pt-BR')}</strong></td>
              <td><FaClock className="text-warning me-1" />{formatDuration(m.duration)}</td>
              <td>{m.days}</td>
            </tr>
          ))}
        </DataTable>
      )}

      {medias.length > 0 && (
        <Card className="mt-4 shadow-sm border-0">
          <Card.Body>
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Label className="fw-bold"><FaUserTie className="me-2" /> Cliente</Form.Label>
                <Form.Select value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
                  <option value="">Selecione um cliente...</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.name} {c.cnpj ? `(${c.cnpj})` : ''}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} className="text-md-end mt-3 mt-md-0">
                <span className="me-3 text-muted">
                  <strong>{selectedMedias.length}</strong> mídias selecionadas
                </span>
                <Button variant="success" size="lg"
                  disabled={!selectedMedias.length || !clienteId || generating}
                  onClick={handleGenerate}>
                  {generating
                    ? <><Spinner size="sm" animation="border" className="me-2" /> Gerando...</>
                    : <><FaFilePdf className="me-2" /> Gerar Relatório PDF</>
                  }
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
